import { useEffect, useRef, useState } from "react";
import {
    Alert,
    Button,
    ButtonGroup,
    Col,
    Form,
    InputGroup,
    Modal,
    Row,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Pencil, PlusCircle, Search } from "react-bootstrap-icons";
import useFetch from "use-http";
import { AuthorityModel, AuthorityTypeModel } from "../../data/Definitions";
import { ConfirmationPopover } from "../../components/ConfirmationPopover";
import DataTable from "react-data-table-component";
import { Formik } from "formik";
import * as yup from "yup";

const AuthoritiesIndex: React.FC = (props) => {
    const [authorities, setAuthorities] = useState<AuthorityModel[]>();
    const [authorityTypes, setAuthorityTypes] =
        useState<AuthorityTypeModel[]>();
    const [filteredAuthorities, setFilteredAuthorities] = useState<
        AuthorityModel[]
    >([]);
    const [selectedAuthorities, setSelectedAuthorities] =
        useState<AuthorityModel | null>(null);
    const [loadingError, setLoadingError] = useState(false);
    const [isFiltering, setIsFiltering] = useState(false);
    const [search, setSearch] = useState("");
    const [addAuthoritiesModalShow, setAddAuthoritiesModalShow] =
        useState(false);
    const addFormRef = useRef<any>(null);
    const [editAuthoritiesModalShow, setEditAuthoritiesModalShow] =
        useState(false);
    const editFormRef = useRef<any>(null);

    const { get, post, put, del, response, loading, error, cache } = useFetch(
        "/api/data/authorities"
    );
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const handleSearchText = (event: any) => {
        setSearch(event.target.value);
    };

    const handleSearch = () => {
        if (search) {
            let s = search
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "");
            var filtered = [...(authorities ?? [])].filter((x) =>
                `${x.id} ${x.name} ${x.nameEn}`
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .includes(s)
            );
            setFilteredAuthorities(filtered);
            setIsFiltering(true);
        } else {
            setIsFiltering(false);
        }
    };

    useEffect(() => {
        handleSearch();
    }, [authorities]);

    useEffect(() => {
        getAuthorities();
    }, []);

    useEffect(() => {
        getAuthorityTypes();
    }, [addAuthoritiesModalShow, editAuthoritiesModalShow]);

    const getAuthorityTypes = async () => {
        var resp = await fetch("/api/data/authorityTypes");
        if (resp.ok) setAuthorityTypes(await resp.json());
    };

    const getAuthorities = async () => {
        setLoadingError(false);
        await getAuthorityTypes();
        let as = await get();
        if (response.ok) setAuthorities(as);
        if (response.redirected) navigate("/login");
        if (error || response.status >= 300) {
            setLoadingError(true);
            return;
        }
    };

    const removeAuthorities = async (id: string) => {
        if (!id) return;
        await del(`/${id}`);
        if (response.ok) {
            cache.delete(`url:/api/data/authorities||method:GET||body:`);
            await getAuthorities();
        }
        if (response.redirected) navigate("/login");
        if (error || response.status >= 300) {
            setLoadingError(true);
            return;
        }
    };

    const validationSchema = yup.object().shape({
        name: yup
            .string()
            .required(t("wtp.validation.required"))
            .typeError(t("wtp.validation.type_error")),
        nameEn: yup
            .string()
            .required(t("wtp.validation.required"))
            .typeError(t("wtp.validation.type_error")),
        authorityTypeId: yup
            .number()
            .min(1, t("wtp.validation.no_select"))
            .required(t("wtp.validation.required"))
            .typeError(t("wtp.validation.type_error")),
    });

    const paginationComponentOptions = {
        rowsPerPageText: t("rows_per_page"),
        rangeSeparatorText: t("rows_per_page_of"),
        selectAllRowsItem: true,
        selectAllRowsItemText: i18n.language === "en" ? "All" : "Όλες",
    };

    const customStyles = {
        headCells: {
            style: {
                fontSize: "16px",
                fontWeight: "bold",
            },
        },
        cells: {
            style: {
                fontSize: "16px",
            },
        },
    };

    const columns = [
        {
            cell: (row: AuthorityModel) => (
                <ButtonGroup>
                    <ConfirmationPopover
                        id={`authorities-confirmation_popover-${row.id}`}
                        header={t("wtp.reuse.confirmation.header")}
                        body={t("wtp.reuse.confirmation.body")}
                        okLabel={t("wtp.reuse.confirmation.ok")}
                        onOk={() => {
                            removeAuthorities(row.id.toString());
                        }}
                        cancelLabel={t("wtp.reuse.confirmation.cancel")}
                        onCancel={() => {}}
                    />
                    <Button
                        size="sm"
                        variant="outline-primary"
                        style={{ border: "none" }}
                        className="d-flex align-items-center justify-content-center m-0"
                        onClick={() => {
                            setSelectedAuthorities(row);
                            setEditAuthoritiesModalShow(true);
                        }}
                    >
                        <Pencil />
                    </Button>
                </ButtonGroup>
            ),
            width: "80px",
        },
        {
            name: t("admin.authorities.authority_name"),
            selector: (row: AuthorityModel) => row.name ?? "",
            sortable: true,
        },
        {
            name: t("admin.authorities.authority_name_english"),
            selector: (row: AuthorityModel) => row.nameEn ?? "",
            sortable: true,
        },
        {
            name: t("admin.authorities.authority_type"),
            selector: (row: AuthorityModel) =>
                authorityTypes?.find((x) => x.id === row.authorityTypeId)
                    ?.name ?? "",
            sortable: true,
        },
    ];

    const handleAddSubmit = async (val: { name: string; nameEn: string }) => {
        (val as any).id = 0;
        await post(val);
        if (response.ok) {
            cache.delete(`url:/api/data/authorities||method:GET||body:`);
            await getAuthorities();
        }
        if (response.redirected) navigate("/login");
        if (error || response.status >= 300) {
            return;
        }
        setAddAuthoritiesModalShow(false);
        setSelectedAuthorities(null);
    };

    const handleEditSubmit = async (val: AuthorityModel) => {
        await put(`/${val.id}`, val);
        if (response.ok) {
            cache.delete(`url:/api/data/authorities||method:GET||body:`);
            await getAuthorities();
        }
        if (response.redirected) navigate("/login");
        if (error || response.status >= 300) {
            return;
        }
        setEditAuthoritiesModalShow(false);
        setSelectedAuthorities(null);
    };

    return (
        <>
            {!loading && !error && authorities && (
                <>
                    <Row>
                        <Col>
                            <h4>{t("admin.authorities")}</h4>
                        </Col>
                        <Col>
                            <ButtonGroup
                                className="float-end btn-group-sm mt-2"
                                aria-label="Toolbar"
                            >
                                <Button
                                    variant="outline-primary"
                                    className="d-flex align-items-center justify-content-center m-0"
                                    onClick={() =>
                                        setAddAuthoritiesModalShow(true)
                                    }
                                >
                                    <PlusCircle className="me-2"></PlusCircle>{" "}
                                    {t("wtp.button.add_upper")}
                                </Button>
                            </ButtonGroup>
                            <InputGroup
                                className="mt-2"
                                size="sm"
                                style={{ width: "350px" }}
                            >
                                <Form.Control
                                    id="search"
                                    name="search"
                                    placeholder={t("search_for")}
                                    onChange={handleSearchText}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            handleSearch();
                                        }
                                    }}
                                    value={search || ""}
                                />
                                <Button
                                    variant="outline-secondary"
                                    id="button-addon2"
                                    onClick={handleSearch}
                                >
                                    <Search />
                                </Button>
                            </InputGroup>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col>
                            <DataTable
                                columns={columns}
                                data={
                                    isFiltering
                                        ? filteredAuthorities
                                        : authorities
                                }
                                pagination
                                // paginationComponent={BootyPagination}
                                customStyles={customStyles}
                                noDataComponent={
                                    <div className="text-center">
                                        {t("wtp.table.no_data")}
                                    </div>
                                }
                                paginationComponentOptions={
                                    paginationComponentOptions
                                }
                            />
                        </Col>
                    </Row>
                </>
            )}
            {!loading && loadingError && (
                <Alert variant="danger">
                    <Alert.Heading>Oh snap! You got an error!</Alert.Heading>
                    <pre>{JSON.stringify(error, null, 4)}</pre>
                </Alert>
            )}
            {loading && (
                <div
                    className="d-flex justify-content-center w-100"
                    style={{ minHeight: "calc(100vh - 294px)" }}
                >
                    <div
                        className="spinner-border"
                        style={{
                            width: "10rem",
                            height: "10rem",
                            marginTop: "200px",
                        }}
                        role="status"
                    >
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}
            <Modal
                show={addAuthoritiesModalShow}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                onHide={() => setAddAuthoritiesModalShow(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        {`${t("wtp.button.add")} ${t("admin.authority")}`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Formik
                        initialValues={{
                            name: "",
                            nameEn: "",
                            authorityTypeId: -1,
                        }}
                        validationSchema={validationSchema}
                        onSubmit={(v) => handleAddSubmit(v)}
                    >
                        {(props) => (
                            <Form
                                noValidate
                                ref={addFormRef}
                                onSubmit={props.handleSubmit}
                            >
                                <Form.Group as={Row} className="mb-2">
                                    <Form.Label
                                        column
                                        className="text-end"
                                        md={{ span: 4 }}
                                    >
                                        {t("admin.authorities.authority_name")}
                                    </Form.Label>
                                    <Col md={{ span: 8 }}>
                                        <Form.Control
                                            placeholder={t(
                                                "admin.authorities.authority_name"
                                            )}
                                            id="name"
                                            name="name"
                                            value={props.values.name}
                                            onChange={props.handleChange}
                                            isInvalid={
                                                (props.touched as any).name &&
                                                !!(props.errors as any).name
                                            }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {(props.errors as any).name}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-2">
                                    <Form.Label
                                        column
                                        className="text-end"
                                        md={{ span: 4 }}
                                    >
                                        {t(
                                            "admin.authorities.authority_name_english"
                                        )}
                                    </Form.Label>
                                    <Col md={{ span: 8 }}>
                                        <Form.Control
                                            placeholder={t(
                                                "admin.authorities.authority_name_english"
                                            )}
                                            id="nameEn"
                                            name="nameEn"
                                            value={props.values.nameEn}
                                            onChange={props.handleChange}
                                            isInvalid={
                                                (props.touched as any).nameEn &&
                                                !!(props.errors as any).nameEn
                                            }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {(props.errors as any).nameEn}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-2">
                                    <Form.Label
                                        column
                                        className="text-end"
                                        md={{ span: 4 }}
                                    >
                                        {t("admin.authorities.authority_type")}
                                    </Form.Label>
                                    <Col md={{ span: 8 }}>
                                        <Form.Select
                                            id="authorityTypeId"
                                            name="authorityTypeId"
                                            value={
                                                props.values.authorityTypeId ??
                                                -1
                                            }
                                            onChange={props.handleChange}
                                            isInvalid={
                                                (props.touched as any)
                                                    .authorityTypeId &&
                                                !!(props.errors as any)
                                                    .authorityTypeId
                                            }
                                        >
                                            <option key={-1} value={-1}>
                                                {t(
                                                    "admin.authorities.select_authority_type"
                                                )}
                                            </option>
                                            {authorityTypes?.map((x, i) => (
                                                <option key={x.id} value={x.id}>
                                                    {x.name} - {x.nameEn}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {
                                                (props.errors as any)
                                                    .authorityTypeId
                                            }
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                            </Form>
                        )}
                    </Formik>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={() =>
                            addFormRef.current?.dispatchEvent(
                                new Event("submit", {
                                    cancelable: true,
                                    bubbles: true,
                                })
                            )
                        }
                    >
                        {t("wtp.button.add")}
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => setAddAuthoritiesModalShow(false)}
                    >
                        {t("wtp.button.cancel")}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={editAuthoritiesModalShow}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                onHide={() => setEditAuthoritiesModalShow(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        {`${t("wtp.button.edit_capitalized")} ${t(
                            "admin.authority"
                        )}`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Formik
                        initialValues={selectedAuthorities!}
                        validationSchema={validationSchema}
                        onSubmit={(v) => handleEditSubmit(v)}
                    >
                        {(props) => (
                            <Form
                                noValidate
                                ref={editFormRef}
                                onSubmit={props.handleSubmit}
                            >
                                <Form.Control
                                    type="hidden"
                                    name="id"
                                    id="id"
                                    value={selectedAuthorities?.id}
                                />
                                <Form.Group as={Row} className="mb-2">
                                    <Form.Label
                                        column
                                        className="text-end"
                                        md={{ span: 4 }}
                                    >
                                        {t("admin.authorities.authority_name")}
                                    </Form.Label>
                                    <Col md={{ span: 8 }}>
                                        <Form.Control
                                            placeholder={t(
                                                "admin.authorities.authority_name"
                                            )}
                                            id="name"
                                            name="name"
                                            value={props.values.name}
                                            onChange={props.handleChange}
                                            isInvalid={
                                                (props.touched as any).name &&
                                                !!(props.errors as any).name
                                            }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {(props.errors as any).name}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-2">
                                    <Form.Label
                                        column
                                        className="text-end"
                                        md={{ span: 4 }}
                                    >
                                        {t(
                                            "admin.authorities.authority_name_english"
                                        )}
                                    </Form.Label>
                                    <Col md={{ span: 8 }}>
                                        <Form.Control
                                            placeholder={t(
                                                "admin.authorities.authority_name_english"
                                            )}
                                            id="nameEn"
                                            name="nameEn"
                                            value={props.values.nameEn}
                                            onChange={props.handleChange}
                                            isInvalid={
                                                (props.touched as any).nameEn &&
                                                !!(props.errors as any).nameEn
                                            }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {(props.errors as any).nameEn}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-2">
                                    <Form.Label
                                        column
                                        className="text-end"
                                        md={{ span: 4 }}
                                    >
                                        {t("admin.authorities.authority_type")}
                                    </Form.Label>
                                    <Col md={{ span: 8 }}>
                                        <Form.Select
                                            id="authorityTypeId"
                                            name="authorityTypeId"
                                            value={
                                                props.values.authorityTypeId ??
                                                -1
                                            }
                                            onChange={props.handleChange}
                                            isInvalid={
                                                (props.touched as any)
                                                    .authorityTypeId &&
                                                !!(props.errors as any)
                                                    .authorityTypeId
                                            }
                                        >
                                            <option key={-1} value={-1}>
                                                {t(
                                                    "admin.authorities.select_authority_type"
                                                )}
                                            </option>
                                            {authorityTypes?.map((x, i) => (
                                                <option key={x.id} value={x.id}>
                                                    {x.name} - {x.nameEn}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {
                                                (props.errors as any)
                                                    .authorityTypeId
                                            }
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                            </Form>
                        )}
                    </Formik>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={() =>
                            editFormRef.current?.dispatchEvent(
                                new Event("submit", {
                                    cancelable: true,
                                    bubbles: true,
                                })
                            )
                        }
                    >
                        {t("wtp.button.update")}
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => setEditAuthoritiesModalShow(false)}
                    >
                        {t("wtp.button.cancel")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AuthoritiesIndex;
